import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ClipboardCheck, Inbox, LogOut, Settings, UserCog, UserPlus } from "lucide-react";
import { api, clearSessions } from "../lib/api";
import { Footer, KonsildentIcon, KonsildentLogo } from "./Brand";
import { Spinner } from "./kd";
import { NotifyBell } from "./Notify";

const IDLE_MS = 30 * 60 * 1000;

function useIdleLogout(onIdle: () => void) {
  const timer = useRef<any>();
  useEffect(() => {
    const reset = () => { clearTimeout(timer.current); timer.current = setTimeout(onIdle, IDLE_MS); };
    const evs = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer.current); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [onIdle]);
}

const navCls = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-[44px] min-w-[40px] items-center justify-center gap-2 rounded-lg px-2 font-medium no-underline transition-colors sm:px-3 ${isActive ? "bg-primary-light text-primary" : "text-text hover:bg-bg"}`;

export function PraxisShell() {
  const [me, setMe] = useState<any>(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() =>
      api.get("/konto/me").then(() => nav("/ueberweiser", { replace: true })).catch(() => nav("/login", { replace: true })));
  }, [nav]);
  const logout = useCallback(async (expired?: boolean) => {
    await api.post("/auth/logout").catch(() => undefined);
    clearSessions();
    window.location.assign(expired === true ? "/login?abgelaufen=1" : "/login");
  }, []);
  const onIdle = useCallback(() => logout(true), [logout]);
  useIdleLogout(onIdle);
  if (!me) return <Spinner />;
  const isAdmin = me.benutzer.rolle === "ADMIN";
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-20 border-b border-border bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-1 px-2 py-2 sm:gap-3 sm:px-4">
          <Link to="/praxis/faelle" className="flex shrink-0 items-center" aria-label="Zur Eingangsliste" data-testid="header-logo">
            <KonsildentLogo className="hidden h-9 sm:block" />
            <KonsildentIcon className="h-9 w-9 sm:hidden" />
          </Link>
          <nav className="flex min-w-0 items-center gap-0 sm:gap-1" aria-label="Hauptnavigation">
            <NavLink to="/praxis/faelle" className={navCls} data-testid="nav-faelle"><Inbox className="h-5 w-5" aria-hidden /><span className="hidden sm:inline">Eingang</span></NavLink>
            <NavLink to="/praxis/ueberweiser/einladen" className={navCls} data-testid="nav-einladungen"><UserPlus className="h-5 w-5" aria-hidden /><span className="hidden sm:inline">Einladungen</span></NavLink>
            <NavLink to="/praxis/pruefung/faelle" className={navCls} data-testid="nav-pruefung"><ClipboardCheck className="h-5 w-5" aria-hidden /><span className="hidden sm:inline">Prüfung</span></NavLink>
            {isAdmin && <NavLink to="/praxis/einstellungen" className={navCls} data-testid="nav-einstellungen"><Settings className="h-5 w-5" aria-hidden /><span className="hidden sm:inline">Einstellungen</span></NavLink>}
            <NavLink to="/praxis/profil" className={navCls} aria-label="Mein Bereich" data-testid="nav-profil"><UserCog className="h-5 w-5" aria-hidden /><span className="hidden whitespace-nowrap 2xl:inline">Mein Bereich</span></NavLink>
            <NotifyBell profil="/praxis/profil" />
            <span className="mx-2 hidden text-sm text-text-muted 2xl:inline" data-testid="header-user">{me.benutzer.vorname} {me.benutzer.nachname} · {me.praxis.name}</span>
            <button onClick={() => logout()} className="flex min-h-[44px] min-w-[40px] items-center justify-center gap-2 rounded-lg px-2 font-medium text-text hover:bg-bg sm:px-3" data-testid="logout-button">
              <LogOut className="h-5 w-5" aria-hidden /><span className="hidden sm:inline">Abmelden</span>
            </button>
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Outlet context={me} />
      </main>
      <Footer />
    </div>
  );
}
